import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
import { queryKeys } from "@/lib/data/static/query-keys";
import type { APIError, MatterLien } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { syncSingleContactConnection } from "@/lib/utils/connections/item-contact-connections";
import {
  getLienContactConnection,
  LIEN_CONTACT_CONNECTION_TYPES,
  type SupportedLienContactRole,
} from "@/lib/utils/connections/lien-contact-connections";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";

type LienContactIdsByRole = Partial<
  Record<SupportedLienContactRole, string | undefined>
>;

export const useSyncLienContactConnections = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      matterId,
      currentLien,
      desiredContactIdsByRole,
      lienId,
    }: {
      currentLien?: MatterLien;
      desiredContactIdsByRole: LienContactIdsByRole;
      lienId: string;
      matterId: string;
    }) => {
      await Promise.all(
        Object.entries(desiredContactIdsByRole).map(([role, contactId]) =>
          syncSingleContactConnection({
            matterId,
            currentConnections: currentLien?.connections,
            connectionType:
              LIEN_CONTACT_CONNECTION_TYPES[role as SupportedLienContactRole],
            current: getLienContactConnection(
              currentLien,
              role as SupportedLienContactRole,
            ),
            desiredContactId: contactId,
            sourceObjectId: lienId,
            sourceObjectType: "matter_lien",
          }),
        ),
      );
    },
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error updating lien connections");
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
  });
};
